import { FC, useCallback, useContext, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router";

import { Stack, Shelf, Grid, useResponsive } from "capitalrx-components-2.0/lib/layout";
import { Body, Subheading, Title } from "capitalrx-components-2.0/lib/typography";
import { Form, SubmitButton, Textbox, useForm } from "capitalrx-components-2.0/lib/forms";

import { BackLink, tinTrim } from "@/components";
import { PROVIDER_MANAGEMENT_LAYOUT } from "@/constants";
import { TinField } from "@/components";
import { ThemeSelectorContext } from "@/context";
import { ANALYTIC_EVENTS, logEvent } from "@/service/analyticsService";
import { getThemeButton, getThemeURL, formatDateWithSlashes, npiValidator } from "@/utils";


export interface MedicalClaimForm {
  provider_npi: string;
  external_member_id: string;
  member_date_of_birth: string;
  provider_tax_id_number: string;
}


export const MedicalClaimsSearchForm: FC = () => {
  const navigate = useNavigate();
  const { primary_color, primary_hover_color } = useContext(ThemeSelectorContext);
  const { twelve } = useResponsive(PROVIDER_MANAGEMENT_LAYOUT);

  const form = useForm<MedicalClaimForm>();
  const { setValue } = form;

  const [searchParams, setSearchParams] = useSearchParams();
  const [originalMemberId, setOriginalMemberId] = useState<string>("");
  const [originalDOB, setOriginalDOB] = useState<string>("");


  useEffect(() => {
    const externalMemberId = searchParams.get("external_member_id");
    const memberDOB = searchParams.get("member_date_of_birth");

    if (externalMemberId) {
      setOriginalMemberId(externalMemberId);
      setValue("external_member_id", externalMemberId);
    }

    if (memberDOB) {
      setOriginalDOB(memberDOB);
      setValue("member_date_of_birth", memberDOB);
    }

    const otherFields: ("provider_npi" | "provider_tax_id_number")[] = [
      "provider_npi",
      "provider_tax_id_number",
    ];

    otherFields.forEach((field) => {
      const value = searchParams.get(field);
      if (value) {
        setValue(field, value);
      }
    });
    setSearchParams({});
  }, [searchParams, setValue]);


  const handleSearch = useCallback(async () => {
    logEvent(ANALYTIC_EVENTS.MEDICAL.claimSearch);

    const formValues = form.getValues();

    const memberId = originalMemberId || formValues.external_member_id;
    const dob = originalDOB || formValues.member_date_of_birth;

    setOriginalMemberId(memberId);
    setOriginalDOB(dob);

    const params = {
      external_member_id: memberId,
      member_date_of_birth: dob,
      provider_npi: formValues.provider_npi,
      provider_tax_id_number: tinTrim(formValues.provider_tax_id_number),
    };

    const encodedParams = new URLSearchParams();
    for (const key in params) {
      if (params[key]) {
        encodedParams.append(key, params[key]);
      }
    }

    navigate({
      pathname: getThemeURL("MEDICAL_CLAIM_LIST"),
      search: encodedParams.toString(),
    });

  }, [navigate, originalMemberId, originalDOB, form]);

  const handleMemberIdFocus = useCallback(() => {
    setValue("external_member_id", originalMemberId);
  }, [originalMemberId, setValue]);

  const handleMemberIdBlur = useCallback(() => {
    const memberId = form.getValues("external_member_id");
    setOriginalMemberId(memberId);
    const replaceVal = memberId.replace(new RegExp("[0-9a-zA-Z]", "g"), "*");
    setValue("external_member_id", replaceVal);
  }, [setValue, form]);

  const handleDOBFocus = useCallback(() => {
    setValue("member_date_of_birth", originalDOB);
  }, [originalDOB, setValue]);

  const handleDOBBlur = useCallback(() => {
    const memberDOB = form.getValues("member_date_of_birth");
    setOriginalDOB(memberDOB);
    const replaceDOBVal = memberDOB.replace(new RegExp("[0-9]", "g"), "*");
    setValue("member_date_of_birth", replaceDOBVal);
  }, [setValue, form]);

  const handleChange = useCallback(() => {
    const memberDOB = form.getValues("member_date_of_birth");
    setOriginalDOB(memberDOB);
    const replaceDOBVal = formatDateWithSlashes(memberDOB);
    setValue("member_date_of_birth", replaceDOBVal);
  }, [setValue, form]);

  return (
    <Form form={form} onSubmit={handleSearch}>
      <Stack spacing="large" style={{ paddingBottom: "8px" }}>
        <Stack spacing="small">
          <Stack spacing="none">
            <BackLink linkText="Home" />
            <Title>Medical Claims & Payments</Title>
          </Stack>
          <Body>Search a billing provider and member to view their claims and payment status</Body>
        </Stack>
        <Stack>
          <Subheading>Provider Details</Subheading>
          <Grid>
            <Grid.Spread columns={twelve}>
              <Textbox.Field
                label="NPI (Billing)"
                name="provider_npi"
                testId="provider_npi"
                validator={npiValidator}
                required
              />
            </Grid.Spread>
            <Grid.Spread columns={twelve}>
              <TinField formName="provider_tax_id_number" designationName="ALL" />
            </Grid.Spread>
          </Grid>
        </Stack>
        <Stack>
          <Subheading>Patient Details</Subheading>
          <Grid>
            <Grid.Spread columns={twelve}>
              <div onFocus={handleMemberIdFocus} onBlur={handleMemberIdBlur} >
                <Textbox.Field
                  label="Member ID"
                  name="external_member_id"
                  testId="external_member_id"
                  required
                  style={{ width: "100%" }}
                />
              </div>
            </Grid.Spread>
            <Grid.Spread columns={twelve}>
              <div onFocus={handleDOBFocus} onBlur={handleDOBBlur} onChange={handleChange}>
                <Textbox.Field
                  instructions="mm/dd/yyyy"
                  required
                  label="Date of Birth"
                  name="member_date_of_birth"
                  placeholder="__/__/____"
                  testId="test__member_date_of_birth"
                  style={{ width: "100%" }}
                />
              </div>
            </Grid.Spread>
          </Grid>
          <Shelf>
            <SubmitButton appearance={getThemeButton("primary", primary_color, primary_hover_color)}>Search</SubmitButton>
            <Shelf.Spacer />
          </Shelf>
        </Stack>
      </Stack>
    </Form>
  );
};
